import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { PhoneValidation } from 'pos-shared/utils/validationHelpers.ts';
import { z } from 'zod';

import { AddressSchema } from './addressSchema.ts';
import { AuditResponseSchema } from './auditSchema.ts';
import { GeoLocationSchema, IdNameSchema, IdSchema } from './commonSchema.ts';
import { CommunicationMethodChannelEnum } from './communicationSchema.ts';
import {
  CustomerAddressFeSchema,
  CustomerCommunicationMethodChannelSchema,
  CustomerCommunicationMethodDefaults,
  CustomerLoyaltyProgramFormSchema,
  CustomerNameFeSchema,
} from './customerFeSchema.ts';
import {
  IndustryEnablerTypeEnumSchema,
  JobItemAuthorisationEnum,
  OrderJobTechAppNotesNoteContentsSchema,
  SlotTypeEnum,
} from './jobSchema.ts';
import { NoteSchema } from './noteSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
  PagedResultSchema,
} from './pagingSchema.ts';
import {
  PaymentMethodEnum,
  PaymentPriceSchema,
  PaymentTypeEnum,
} from './paymentSchema.ts';
import {
  AdditionalExpenseGroupSchema,
  JobDiscountSchema,
  OrderDiscountSchema,
  OrderTotalSchema,
  PriceSchema,
  TotalPricingSchema,
  UnitSellPricingSchema,
} from './pricingSchema.ts';
import { LiteralStaticConfigSchema } from './staticConfigSchema.ts';
import {
  B2BAIStatusEnum,
  CustomerTypeEnum,
  OrderCreationSourceEnum,
} from './statusSchema.ts';
import { StockMeasurementsSchema } from './stockSchema.ts';
import {
  FulfilmentToAttributesSchema,
  VehicleGeneralAttributesSchema,
  VehicleSchema,
} from './vehicleSchema.ts';
import {
  WarrantySchema,
  WarrantyTireLabelPrintSchema,
} from './warrantiesSchema.ts';

// Note: if you want to add custom validation messages,
// suggest you don't add them here!
// Instead, interpret them somehow by using the zod keys
// e.g. t('validation', `${fieldName}.${errorCode}`)

const OrderSupplierSchema = z.object({
  supplierId: z.uuid(),
  supplierName: z.string().nullish(),
  stockCode: z.string().nullish(),
  leadTime: z.number(),
});

///// BACKEND SCHEMAS....
export const GetOrderRequestSchema = z.object({
  id: z.uuid(),
  includeCustomer: z.boolean().optional(),
  includeJobAnswers: z.boolean().optional(),
  includeJobs: z.boolean().optional(),
  includeOrderNotes: z.boolean().optional(),
  includeJobItems: z.boolean().optional(),
  includeJobFulfilmentTechnicians: z.boolean().optional(),
  includeOrderSchedules: z.boolean().optional(),
  includeCreditReleaseStatus: z.boolean().optional(),
  includeOrderAlerts: z.boolean().optional(),
  includeTireLabels: z.boolean().optional(),
});

export const OrderScheduleSchema = z
  .object({
    service: z.string(),
    appointmentDuration: z.string(),
    servicePrice: z.string(),
    slotType: SlotTypeEnum,
    appointmentDate: z.coerce.date<Date>(),
  })
  .extend(IdSchema.shape);

export const OrderIdSchema = z.object({
  orderId: z.uuid(),
});

export const OrderIdNumberSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string().nullish(),
});

export const OrderScheduleDetailsSchema = z.object({
  id: z.uuid(),
  timeslotGuid: z.uuid(),
  jobIds: z.array(z.uuid()).nullish(),
  durationMins: z.int(),
  durationFormatted: z.string().nullish(),
  location: z.string().nullish(),
  fulfilmentType: z.string().nullish(),
  scheduleCustomer: z
    .object({
      start: z.string(),
      end: z.string(),
      display: z.string().nullish(),
      slotType: z.string().nullish(),
      finalOptimisationComplete: z.boolean(),
    })
    .nullish(),
  scheduleFulfilment: z
    .object({
      start: z.string(),
      end: z.string(),
      display: z.string().nullish(),
      slotType: z.string().nullish(),
      finalOptimisationComplete: z.boolean(),
    })
    .nullish(),
  jobFulfilmentAddress: z.object({
    serviceLocationId: z.string().nullish(),
    summary: z.string().nullish(),
    text: z.string().nullish(),
    addressLine1: z.string().nullish(),
    addressLine2: z.string().nullish(),
    addressLine3: z.string().nullish(),
    addressLine4: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    countryCode: z.string().nullish(),
    timeZone: z.string().nullish(),
    geoLocation: GeoLocationSchema,
  }),
  executionReady: z.boolean(),
  bookingReference: z.string().nullish(),
  estimatedCompletionTime: z.coerce.date<Date>().nullish(),
});

export const OrderCustomerDetailsFeSchema = z.object({
  customerId: z.uuid(),
  customerReference: z.string(),
  customerType: CustomerTypeEnum,
  customerName: CustomerNameFeSchema,
  address: z.string().optional(),
  billingAddress: AddressSchema.nullish(),
  email: z.string().nullish(),
  additionalEmail: z.string().nullish(),
  phone: z.string().nullish(),
  additionalPhone: z.string().nullish(),
  employeeId: z.string().nullish(),
  customerCommunicationMethods: z
    .array(CustomerCommunicationMethodChannelSchema)
    .nullish(),
});

export const OrderDriverDetailsFeSchema = z.object({
  driverId: z.uuid(),
  orderId: z.uuid().optional(),
  name: z.string(),
  email: z.string().optional(),
  contactNumber: z.string(),
});

export const OrderDriverSearchRequestSchema = z.object({
  searchText: z.string().min(1),
  accountNumber: z.string(),
  resultsPerPage: z.number().nonnegative().optional(),
  pageNumber: z.number().nonnegative().optional(),
});

export const OrderDriverSearchDetailsContactPreferenceSchema = z.enum([
  'Email',
  'Sms',
]);

export const OrderDriverSearchDetailsSchema = z.object({
  id: z.uuid().optional(),
  driverId: z.uuid().optional(),
  orderId: z.uuid().optional(),
  name: z.string(),
  email: z.string().optional(),
  contactNumber: z.string(),
  companyName: z.string().optional(),
  fleetId: z.string().optional(),
  contactPreference: z
    .array(OrderDriverSearchDetailsContactPreferenceSchema)
    .optional(),
});

export const OrderDriverSearchResponseSchema = z.object({
  totalResults: z.int(),
  totalPages: z.int(),
  results: z.array(OrderDriverSearchDetailsSchema),
});

export const OrderPreventativeMaintenanceRequestSchema = z.object({
  orderId: z.uuid(),
  serviceLocationId: z.uuid(),
});

export const OrderPreventativeMaintenanceResponseSchema = z.object({
  preventativeMaintenanceItems: z
    .array(
      z.object({
        serviceCodeId: z.number(),
        displayName: z.string(),
      })
    )
    .nullish(),
  validationError: z.string().nullish(),
});

export const UpdateOrderStatusRequestSchema = z
  .object({
    orderId: z.uuid(),
    status: z.string(),
    reasonForChange: z.string().optional(),
  })
  .refine(
    (data) => (data.status === 'Cancelled' ? !!data.reasonForChange : true),
    {
      path: ['reasonForChange'],
    }
  );

///// FRONTEND-BFF SCHEMAS....
export const OrderSourceEnum = z.enum(['Walk In', 'Web', 'Contact Center']);
export const OrderSourceCamelCase = z.enum(
  arrayLiteralToCamelCase(OrderSourceEnum.options)
);
export const CustomerTypeSchema = z.object({
  customerType: CustomerTypeEnum,
});
export const CreateOrderPart1FeSchema = z
  .object({
    customerId: z.uuid(),
    fleetOrderCustomerId: z.string().optional(),
    fleetOrderCompanyName: z.string().optional(),
    fleetOrderAccountNumber: z.string().optional(),
  })
  .extend(CustomerTypeSchema.shape);
export const CreateOrderPart2FeSchema = z
  .object({
    vehicle: VehicleSchema.nullish(),
    taxExemption: z
      .object({
        reference: z.string().nullish(),
        type: z.string().nullish(),
        isRedeemed: z.boolean().nullish(),
      })
      .nullish(),
    isFetExempt: z.boolean().optional(),
    flyBuys: z.string().nullish(),
    locationId: z.string().min(1),
  })
  .extend(CustomerLoyaltyProgramFormSchema.shape);

export const CreateFleetOrderPart1FeSchema = z
  .object({
    customerId: z.uuid().optional(),
    fleetOrderCustomerId: z.string(),
    fleetOrderCompanyName: z.string(),
    fleetOrderAccountNumber: z.string(),
  })
  .extend(CustomerTypeSchema.shape);

export const FleetDriverSchema = z.object({
  driverId: z.uuid().optional(),
  id: z.string().optional(),
  orderId: z.uuid().optional(),
  name: z.string(),
  contactNumber: z.string().regex(PhoneValidation),
  email: z.union([z.email(), z.literal('')]).optional(),
  companyName: z.string().max(30).optional(),
  contactPreference: z
    .array(OrderDriverSearchDetailsContactPreferenceSchema)
    .optional(),
  fleetId: z.string().optional(),
  retainDriver: z.boolean().optional(),
});

export const FleetDriverFormSchema = FleetDriverSchema.omit({
  contactPreference: true,
}).extend({
  contactPreference: z.object({
    sms: z.boolean(),
    email: z.boolean(),
  }),
});

export const FleetDriverIdSchema = z.object({
  driverId: z.uuid(),
});

export const AttachFleetDriverToOrderSchema = z.object({
  orderId: z.uuid(),
  driverId: z.uuid(),
});

export const AttachFleetDriverToOrderResponseSchema = z.object({
  orderId: z.uuid(),
  driverId: z.uuid(),
  isVinDefined: z.boolean().optional(),
});

export const CompleteFleetOrderErrorSchema = z.object({
  errorId: z.string(),
  errorMessage: z.string(),
  fieldName: z.string().optional(),
  lineNumber: z.string().optional(),
  value: z.string().optional(),
  partId: z.uuid().optional(),
  altered: z.boolean().optional(),
});

export const FleetTreadnetPriceUpdatesSchema = z.object({
  partId: z.uuid(),
  oldPrice: z.number(),
  newPrice: z.number(),
  description: z.string().optional(),
  partNumber: z.string().optional(),
});

export const CompleteFleetOrderResponseSchema = z.object({
  status: z.enum(['Success', 'Failure']),
  message: z.string(),
  errorCode: z.string().optional(),
  headerErrors: z.array(CompleteFleetOrderErrorSchema).optional(),
  lineItemErrors: z.array(CompleteFleetOrderErrorSchema).optional(),
  b2BTreadnetPriceUpdates: z.array(FleetTreadnetPriceUpdatesSchema).optional(),
  hasErrors: z.boolean().optional(),
});

export const CreateOrderFeSchema = z
  .object({})
  .extend(CreateOrderPart1FeSchema.shape)
  .extend(CreateOrderPart2FeSchema.shape);

export const CreateFleetOrderFeSchema = z
  .object({})
  .extend(CreateFleetOrderPart1FeSchema.shape)
  .extend(CreateOrderPart2FeSchema.shape);

export const CreateRetailFleetOrderFeSchema = z.union([
  CreateOrderFeSchema,
  CreateFleetOrderFeSchema,
]);

const AddOrderSpecificAttributesSchema = z.object({
  orderId: z.uuid(),
  data: FulfilmentToAttributesSchema.nullish(),
});

const AddOrderFulfilmentToSchema = z.object({
  id: z.uuid(),
  dataType: z.string().nullish(),
  reference: z.string().nullish(),
  referenceStatic: z.string().nullish(),
  description: z.string().nullish(),
  generalAttributes: z.array(VehicleGeneralAttributesSchema).nullish(),
  orderSpecificAttributes: z.array(AddOrderSpecificAttributesSchema).nullish(),
  dateCreated: z.string(),
});

export const AddOrderSourceSchema = z.object({
  fulfilmentBaseLocation: z.uuid(),
  customerSourcePrimary: z.enum(['WalkIn', 'Web', 'ContactCentre']),
  customerSourceSecondary: z.string().nullish(),
  associateToOrderId: z.uuid(),
});

const AddOrderCustomerSchema = z.object({
  customerType: CustomerTypeEnum.nullish(),
  customerId: z.uuid().nullish(),
  customerReference: z.string().nullish(),
  customerEmailAddress: z.string(),
  customerAdditionalEmailAddress: z.string(),
  customerPhoneNumber: z.string().nullish(),
  customerName: CustomerNameFeSchema.omit({ fullName: true }),
  fleetAccount: z
    .object({
      accountNumber: z.string().nullish(),
      companyName: z.string().nullish(),
    })
    .nullish(),
  fulfilmentToReference: z.string().nullish(),
  source: AddOrderSourceSchema.nullish(),
  taxExemption: z
    .object({
      reference: z.string().nullish(),
      type: z.string().nullish(),
    })
    .nullish(),
  isFetExempt: z.boolean().optional(),
  communicationPreference: z
    .object({
      id: z.uuid(),
      isCurrent: z.boolean(),
      allowInternalMarketing: z.boolean(),
      allow3rdPartyMarketing: z.boolean(),
      channels: CommunicationMethodChannelEnum.array().nullish(),
      channelsUsageMode: z.enum(['And', 'Or']),
    })
    .nullish(),
  communicationMethodDefaults: CustomerCommunicationMethodDefaults.nullish(),
  flyBuys: z.string().length(16).nullish(),
});

export const AddOrderRequestSchema = z.object({
  customer: AddOrderCustomerSchema,
  fulfilmentTo: AddOrderFulfilmentToSchema.nullish(),
  source: AddOrderSourceSchema,
  status: z.string(),
});

export const OrderJobsForPaymentPartsSchema = z.object({
  id: z.uuid().nullish(),
  description: z.string().nullish(),
  quantity: z.number().nonnegative().nullish(),
  authorisedUnitSellPrice: PaymentPriceSchema.nullish(),
  productTaxCode: z.string().optional(),
  partNumber: z.string().optional(),
  additionalExpenses: z
    .object({
      Tax: AdditionalExpenseGroupSchema.optional(),
      Fee: AdditionalExpenseGroupSchema.optional(),
    })
    .optional(),
});

export const OrderJobItemPartTypeEnum = z.enum([
  'Part',
  'Labor',
  'Timeslot',
  'Fee',
  'Warranty',
]);

export const OrderJobItemPartOrderedUnitSchema = z.object({
  id: z.uuid().nullish(),
  purchaseOrder: z.uuid().nullish(),
  stockUnitId: z.uuid().nullish(),
  status: z.string().nullish(),
  supplierConfirmation: z.string().nullish(),
});

export const OrderJobItemPartFeSchema = z.object({
  jobId: z.uuid(),
  orderId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  type: OrderJobItemPartTypeEnum,
  description: z.string(),
  partNumber: z.string().nullish(),
  itemAuthorisation: JobItemAuthorisationEnum,
  numberUnitsOrdered: z.number().nonnegative().nullish(),
  unitQuantity: z.number().nonnegative(),
  sku: z.string().nullish(),
  unitBuyPrice: PriceSchema.nullish(),
  unitSellPrice: UnitSellPricingSchema,
  isFixedLabor: z.boolean().nullish(),
  industryEnablerData: z.any().nullish(),
  status: z.string().nullish(),
  productTaxCode: z.string().nullish(),
  additionalExpenses: z
    .object({
      Tax: AdditionalExpenseGroupSchema.optional(),
      Fee: AdditionalExpenseGroupSchema.optional(),
    })
    .optional(),
  index: z.object({
    jobIndex: z.number(),
    itemIndex: z.number(),
    partIndex: z.number(),
  }),
  notes: NoteSchema.array().nullish(),
  supplier: OrderSupplierSchema.pick({
    supplierId: true,
    supplierName: true,
  }).nullish(),
  isStockTracked: z.boolean().nullish(),
  hasInternalStock: z.boolean(),
  numberOfUnitsFromInternalStock: z.number(),
  orderedUnits: OrderJobItemPartOrderedUnitSchema.array().nullish(),
  dotCodes: z.string().array().nullish(),
  refundDetails: z.string().optional(),
  refundReasonCode: z.string().optional(),
});

export const OrderJobItemSellTotalSchema = z.object({
  labour: PriceSchema,
  quoted: PriceSchema,
  authorised: PriceSchema,
  discount: z
    .object({
      id: z.uuid().nullish(),
      code: z.string().nullish(),
      reason: z.string().nullish(),
      total: TotalPricingSchema.nullish(),
    })
    .nullish(),
});

export const OrderJobItemFeSchema = z.object({
  jobId: z.uuid(),
  orderId: z.uuid(),
  itemId: z.uuid(),
  name: z.string().nullish(),
  status: z.string(),
  description: z.string().nullish(),
  authorised: z.boolean(),
  authorisation: JobItemAuthorisationEnum.nullish(),
  authorisationStatus: z.string().nullish(),
  productTaxCode: z.string().optional(),
  reasonForAddition: z
    .object({
      description: z.string().nullish(),
      reasonCode: z.string().nullish(),
    })
    .nullish(),
  schedulingConstraints: z.object({
    totalDuration: z.number(),
    assetConstraints: z.string().nullish(),
  }),
  itemLineSellTotal: OrderJobItemSellTotalSchema,
  parts: OrderJobItemPartFeSchema.array(),
  industryEnablerData: z.any().nullish(),
  warranty: WarrantySchema.array(),
  measurements: StockMeasurementsSchema.nullish(),
  index: z.object({
    jobIndex: z.number(),
    itemIndex: z.number(),
  }),
});

export const OrderJobTechnicianFeSchema = z.object({
  userId: z.uuid(),
  name: z.string().nullish(),
  effortPercentage: z.number().min(0).max(100),
  qcGroupId: z.string().nullish(),
  qcRatingLevel: z.number().nonnegative(),
});

export const TechAppNotesFeSchema = z.object({
  noteContents: OrderJobTechAppNotesNoteContentsSchema.array(),
  noteDate: z.string().nullish(),
});

export const DiscountBeSchema = z.object({
  code: z.string(),
  exTax: z.number(),
  tax: z.number(),
});

export const OrderJobPromotionDetailsFeSchema = z.object({
  promotionName: z.string(),
  promotionId: z.string(),
  amountOrRate: z.number(),
});

export const AIFleetCreationSouceSchema = z.enum([
  'CustomerRequest',
  'EndOfLife',
  'PreventativeMaintenance',
  'ServiceDue',
  'Other',
]);

export const FleetRetailCombinedEnum = z.enum([
  ...AIFleetCreationSouceSchema.options,
  ...OrderCreationSourceEnum.options,
]);

export type AIFleetCreationSourceType = z.infer<
  typeof AIFleetCreationSouceSchema
>;

export const UpdateFleetOrRetailCreationSourceSchema = z.object({
  creationSource: z.union([
    AIFleetCreationSouceSchema,
    OrderCreationSourceEnum,
  ]),
});

export type UpdateFleetOrRetailCreationSourceType = z.infer<
  typeof UpdateFleetOrRetailCreationSourceSchema
>;

export const OrderJobFeSchema = z.object({
  jobId: z.uuid(),
  reference: z.string().nullish(),
  description: z.string().nullish(),
  fulfilmentLocation: z.uuid(),
  status: z.string().nullish(),
  sellTotal: z.number().nullish(),
  promotionDetails: z.array(OrderJobPromotionDetailsFeSchema).nullish(),
  dateScheduled: z.string().nullish(),
  dateCreated: z.string(),
  location: z.string().nullish(),
  fulfilmentType: z.string().nullish(),
  jobType: z.string().nullish(),
  creationSource: FleetRetailCombinedEnum.prefault('CustomerRequest'),
  scheduleCustomer: z.any().nullish(), // ignore for now
  scheduleFulfilment: z.any().nullish(), // ignore for now
  industryEnablerType: IndustryEnablerTypeEnumSchema.nullish(),
  industryEnablerData: z.any().nullish(),
  items: z.array(OrderJobItemFeSchema), // NOT optional
  technicians: OrderJobTechnicianFeSchema.array().prefault([]),
  notes: z.array(NoteSchema).nullish(),
  techAppNotes: TechAppNotesFeSchema.nullish(),
  discount: DiscountBeSchema.nullish(),
  total: OrderTotalSchema,
  sequenceNumber: z.number().nullish(),
});

export const OrderTypeEnum = z.enum(['Refund', 'Purchase']);

export const OrderCreditReleaseStatusSchema = z.object({
  isRequired: z.boolean().nullish(),
  isRequiredMessage: z.string().nullish(),
  isRequiredReason: z.string().nullish(),
});

export const B2BAIApprovalSchema = z.object({
  aiReference: z.string().nullish(),
  status: B2BAIStatusEnum,
  altered: z.boolean(),
  sentDate: z.coerce.date<Date>().nullish(),
  rejectedItems: z
    .array(
      z.object({
        itemId: z.uuid(),
        description: z.string().nullish(),
      })
    )
    .nullish(),
});

export const B2BOrderStatus = z.object({
  b2BTreadNetStatus: z.object({
    status: z.string(),
    message: z.string(),
    errorCode: z.string(),
    drNumber: z.string(),
    headerErrors: z
      .object({
        errorId: z.string(),
        errorMessage: z.string(),
        fieldName: z.string(),
      })
      .array()
      .nullish(),
    lineItemErrors: z
      .object({
        errorId: z.string(),
        errorMessage: z.string(),
        fieldName: z.string(),
        lineNumber: z.string(),
        value: z.string(),
      })
      .array()
      .nullish(),
  }),
});

export const OrderJobPartsAlertMetadataSchema = z.object({
  jobId: z.uuid(),
  jobDescription: z.string().nullish(),
  jobReference: z.string().nullish(),
  partId: z.uuid(),
  partDescription: z.string().nullish(),
  purchaseOrder: z.string().nullish(),
  quantity: z.number(),
});

export const PurchaseOrderNotReceivedMetadataSchema = z.array(z.string());

export const OrderAlertCodeEnum = z.enum([
  'None',
  'PurchaseOrderRequiredForPunchoutJobs',
  'PurchaseOrderNotReceived',
  'StockNotAssociated',
]);

export const OrderAlertSchema = z.object({
  code: OrderAlertCodeEnum,
  message: z.string(),
  isActive: z.boolean(),
  metadata: OrderJobPartsAlertMetadataSchema.array().nullish(),
});

export const OrderJobAlertSchema = OrderJobPartsAlertMetadataSchema.extend({
  alert: z.string(),
});

export const OrderFeSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string().min(1),
  customerId: z.uuid(),
  address: z.string().optional(),
  currencyCode: z.string().nullish(),
  version: z.number(),
  referenceId: z.string().nullish(), // reference from another order (main order)
  orderType: OrderTypeEnum.nullish(),
  source: LiteralStaticConfigSchema,
  status: z.string().nullish(),
  statusReason: z.string().nullish(),
  dateCreated: z.coerce.date<Date>(),
  fulfilmentToId: z.uuid().optional(),
  fulfilmentToReference: z.string().optional(),
  fulfilmentBaseLocationId: z.uuid(),
  fleetAccountId: z.uuid().optional(),
  timeZone: z.string().nullish(),
  jobsCount: z.number().nonnegative(),
  customer: OrderCustomerDetailsFeSchema.optional(),
  jobAnswers: z.array(z.any()).optional(), // Coming soon
  notes: z.array(NoteSchema),
  isRefundEligible: z.boolean(),
  jobs: OrderJobFeSchema.array(), // always load the jobs, not optional
  orderSchedules: OrderScheduleDetailsSchema.array().optional(),
  taxExemption: z
    .object({
      reference: z.string().nullish(),
      type: z.string().nullish(),
      isRedeemed: z.boolean().nullish(),
    })
    .nullish(),
  total: OrderTotalSchema,
  creditReleaseStatus: OrderCreditReleaseStatusSchema.nullish(),
  b2BOrderStatus: z
    .object({
      b2BTreadNetStatus: CompleteFleetOrderResponseSchema.optional(),
      b2BAIStatus: B2BAIApprovalSchema.nullish(),
    })
    .optional(),
  alerts: OrderAlertSchema.array(),
  tireLabels: WarrantyTireLabelPrintSchema.array(),
  flyBuys: z.string().optional(),
});

export const OverrideOrderJobFormPartSchema = z.object({
  partId: z.uuid(),
  partNumber: z.string().nullish(),
  type: OrderJobItemPartTypeEnum.optional(),
  description: z.string().min(1),
  unitQuantity: z.number(),
  price: z.number().multipleOf(0.01),
  status: z.string().nullish(),
  promotions: z.string().nullish(),
  itemIndex: z.number(),
  partIndex: z.number(),
});

const OverrideOrderJobFormItemSchema = z.object({
  itemId: z.uuid(),
  duration: z.number().nonnegative(),
  parts: OverrideOrderJobFormPartSchema.array(),
});

export const OverrideOrderJobFormSchema = z.object({
  jobId: z.uuid(),
  items: OverrideOrderJobFormItemSchema.array(),
});

export const OverrideOrderJobRequestSchema = z.object({
  orderId: z.uuid(),
  items: OrderJobItemFeSchema.array(),
  formData: OverrideOrderJobFormSchema,
});

export const FleetJobPricingErrorSchema = z.object({
  partName: z.string().nullish(),
  partCode: z.string().nullish(),
  reasonCode: z
    .enum(['SET_PRICE_EXCEEDED', 'MIN_PRICE_EXCEEDED', 'MAX_PRICE_EXCEEDED'])
    .nullish(),
});
export const FleetUpdateJobErrorSchema = z.object({
  problemDetails: z.object({
    message: z.object({
      problems: z.array(FleetJobPricingErrorSchema),
    }),
  }),
});

export const OrderJobsForPaymentItemsSchema = z.object({
  jobId: z.uuid(),
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string().optional(),
      parts: z.array(OrderJobsForPaymentPartsSchema),
    })
  ),
  authorisedSellTotal: PaymentPriceSchema,
  discount: OrderDiscountSchema.optional(),
  productTaxCode: z.string().optional(),
});

export const OrderJobsForPaymentSchema = z.object({
  orderId: z.uuid(),
  jobs: z.array(OrderJobsForPaymentItemsSchema),
});

export const OrderJobsForPaymentPartsFeSchema = z.object({
  partId: z.uuid().nullish(),
  description: z.string().nullish(),
  quantity: z.number().nonnegative().nullish(),
  authorisedUnitSellPrice: PriceSchema,
});

export const OrderJobsForPaymentItemsFeSchema =
  OrderJobsForPaymentItemsSchema.omit({ items: true }).extend({
    items: z.array(
      z.object({
        name: z.string().nullish(),
        itemId: z.string(),
        parts: z.array(OrderJobsForPaymentPartsFeSchema),
      })
    ),
  });

export const OrderJobsDiscountItemFESchema =
  OrderJobsForPaymentItemsFeSchema.extend({
    description: z.string(),
    sellTotal: z.number(),
  });

export const AddOrderJobItemPaymentSchema = z.object({
  orderId: z.uuid(),
  jobItemIds: z.string().array().optional(),
  id: z.string().optional(),
  paymentMethod: PaymentMethodEnum,
  paymentReference: z.string(),
  paymentDate: z.coerce.date<Date>(),
  cardType: z.string().optional(),
  chequeNumber: z.string().optional(),
  chequeAuthorisationCode: z.string().optional(),
  originalPaymentTransactionId: z.string().optional(),
  giftCardNumber: z.string().optional(),
  pinNumber: z.string().optional(),
  expiryDate: z.coerce.date<Date>().optional(),
  type: PaymentTypeEnum,
  amount: PaymentPriceSchema,
  tenderAmount: z.number().optional(),
  reason: z.string().optional(),
  reasonCode: z.string().optional(),
  traceData: z.object({}).catchall(z.unknown()).optional(),
  linkMobileNumber: z.string().optional(),
});

export const AddOrderJobItemResponseSchema = AddOrderJobItemPaymentSchema.omit({
  orderId: true,
});

export const OrderAdditionalPropertiesSchema = z.object({
  orderId: z.uuid(),
  properties: z.object({}).catchall(z.unknown()).optional(),
});

export const OrderValidateDiscountRequestSchema = z.object({
  discountCode: z.string(),
});

export const OrderFlybuysResponseSchema = z.object({
  number: z.string().nullable(),
  editable: z.boolean(),
});
export type OrderFlybuysResponseModel = z.infer<
  typeof OrderFlybuysResponseSchema
>;
export const UpdateOrderFlybuysRequestSchema = z.object({
  orderId: z.string().uuid(),
  number: z.string(),
});
export type UpdateOrderFlybuysRequestModel = z.infer<
  typeof UpdateOrderFlybuysRequestSchema
>;

export const OrderBillingJobItemSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  description: z.string(),
  status: z.string(),
  quantity: z.number(),
  authorisedUnitSellPrice: PriceSchema,
  discount: JobDiscountSchema,
  productTaxCode: z.string().optional(),
  additionalExpenses: z
    .object({
      Tax: AdditionalExpenseGroupSchema.optional(),
      Fee: AdditionalExpenseGroupSchema.optional(),
    })
    .optional(),
});

export const OrderBillingJobSchema = z.object({
  jobId: z.uuid(),
  description: z.string(),
  authorisedSellTotal: TotalPricingSchema,
  discount: JobDiscountSchema,
  stateFees: z.number().optional(),
  fetFees: z.number().optional(),
  shopSupplies: z.number().optional(),
  items: OrderBillingJobItemSchema.array(),
});

export const OrderBillingSchema = z.object({
  orderId: z.uuid(),
  jobs: OrderBillingJobSchema.array(),
});

export const OrderPaymentsAdapterTypeEnum = z.union([
  PaymentMethodEnum,
  z.literal('accountZip'),
  z.literal('accountLookup'),
  z.literal('accountCharge'),
]);

export const OrderPaymentsAdapterSchema = z.object({
  messageType: z.literal('payment'),
  type: OrderPaymentsAdapterTypeEnum,
  orderId: z.string(),
  invoiceNumber: z.string(),
  txReference: z.number(),
  tenderAmount: z.number(),
  countryCode: z.string(),
  currencyCode: z.string(),
  operatorId: z.string(),
  promptText: z.string().nullish(),
  zip: z.string().nullish(),
  token: z.string().nullish(),
});

export const OrderVoidsAdapterSchema = z.object({
  messageType: z.literal('void'),
  modalType: z.string(),
  cardType: z.string().nullish(),
  type: PaymentMethodEnum,
  invoiceNumber: z.string(),
  traceData: z.record(z.union([z.string(), z.number()]), z.unknown()).nullish(),
});

export const OrderGiftCardAdapterSchema = z.object({
  type: z.literal('giftCard'),
  orderId: z.uuid(),
  messageType: z.enum(['payment', 'balance']),
  invoiceNumber: z.string(),
  transactionRef: z.number(),
  tenderAmount: z.number(),
  secretPin: z.string().optional(),
  accountNo: z.string().optional(),
});

export const OrderRefundsAdapterSchema = z.object({
  promptText: z.string(),
  messageType: z.literal('refund'),
  type: z.string(),
  orderId: z.string(),
  invoiceNumber: z.string(),
  txReference: z.number(),
  tenderAmount: z.number(),
  countryCode: z.string(),
  currencyCode: z.string(),
  operatorId: z.string(),
});

export const OrderRefundWithTokenAdapterSchema = z.object({
  messageType: z.literal('refund'),
  type: z.literal('token'),
  tenderAmount: z.number(),
  txReference: z.number(),
  token: z.string(),
  operatorId: z.string(),
  currencyCode: z.string(),
  countryCode: z.string(),
  invoiceNumber: z.string(),
});

export const SendOrderInvoiceSchema = z.object({
  orderId: z.uuid(),
  email: z.string().nullish(),
});

export const OrderSearchRequestFeSchema = z
  .object({
    searchText: z.coerce.string<string>().optional(),
    appointmentDate: z.string().optional(),
    minAppointmentDate: z.string().optional(),
    maxAppointmentDate: z.string().optional(),
    locationId: z.uuid().optional(),
    orderStatuses: z.array(z.string()).optional(),
    customerType: CustomerTypeEnum.optional(),
    customerId: z.uuid().optional(),
    fleetAccountNumber: z.string().optional(),
    technicianId: z.uuid().optional(),
  })
  .extend(PagedRequestSchema.shape);

export const OrderSearchRequestV2FeSchema = z
  .object({
    Wildcard: z.coerce.string<string>().optional(),
    ScheduledDate: z.string().optional(),
    ServiceLocationId: z.uuid().optional(),
    Status: z.array(z.string()).optional(),
    customerType: CustomerTypeEnum.optional(),
    customerId: z.uuid().optional(),
    aiStatus: z.array(z.string()).optional(),
  })
  .extend(PagedRequestSchema.shape);

export const OrderSearchResultV2BeSchema = z.object({
  orderId: z.string(),
  orderReference: z.string(),
  type: z.union([z.literal('0'), z.literal('1')]),
  customerType: CustomerTypeEnum,
  customer: z.string(),
  vehicle: z.string().optional(),
  scheduledDate: z.coerce.date<Date>().optional(),
  status: z.string(),
  source: z.union([z.literal('0'), z.literal('1'), z.literal('2')]).optional(),
  serviceLocationId: z.string(),
  customerId: z.string(),
  statusDateUpdated: z.coerce.date<Date>().optional(),
  dateCreated: z.coerce.date<Date>().optional(),
  aiStatus: z.string().optional(),
});

export const OrderSearchResultV2FeSchema = OrderSearchResultV2BeSchema.omit({
  source: true,
  type: true,
}).extend({
  source: OrderSourceEnum.optional(),
});

export const OrderSearchResponseV2BeSchema = createPaginatedResponseSchema(
  OrderSearchResultV2BeSchema
);

export const OrderSearchResultFeSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string().min(1),
  customerId: z.uuid(),
  customerName: CustomerNameFeSchema,
  customerType: CustomerTypeEnum,
  jobReferences: z.array(
    z.object({
      jobId: z.uuid(),
      reference: z.string().min(1),
    })
  ),
  vrn: z.string().nullish(), // fulfilmentToReference
  vehicle: VehicleSchema.omit({ id: true }).optional(),
  vehicleYear: z.string().nullish(),
  vehicleMake: z.string().nullish(),
  vehicleModel: z.string().nullish(),
  mileage: z.string().nullish(),
  locationId: z.uuid().nullish(),
  locationName: z.string().nullish(),
  status: z.string().nullish(),
  source: z.string(),
  appointmentDate: z.coerce.date<Date>().nullish(),
  dateCreated: z.coerce.date<Date>(),
});

export const OrderSearchResponseFiltersSchema = z.object({
  customerTypes: IdNameSchema.array().optional(),
  serviceLocations: IdNameSchema.array().optional(),
});

export const OrderSearchResponseFeSchema = z
  .object({
    results: z.array(OrderSearchResultFeSchema),
    filters: OrderSearchResponseFiltersSchema,
  })
  .extend(PagedResultSchema.shape);

// This is for orders pre-Avayler
// Keys are not in camelCase (Not ideal)
export const HistoricOrderschema = z.object({
  orderreference: z.string(),
  customerreference: z.string(),
  vehicle: z.string(),
  vin: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  sub_Model: z.string().nullish(),
  model_Year: z.string().nullish(),
  color: z.string().nullish(),
  mileage_Out: z.string().nullish(),
  invoice_Date: z.coerce.date<Date>(),
  sls_Tran_Part_Appr_Amt: z.string().nullish(),
  sls_Tran_Lab_Appr_Amt: z.string().nullish(),
  sls_Tran_Shop_Supp_Amt: z.string().nullish(),
  sls_Tran_Gift_Card_Amt: z.string().nullish(),
  job_Type: z.string().nullish(),
});

export const SearchHistoricOrdersRequestSchema = z.object({
  customerReference: z.string(),
  pageNumber: z.number().nonnegative(),
  resultsPerPage: z.number().nonnegative(),
  invoiceDateFrom: z.coerce.date<Date>().optional(),
  invoiceDateTo: z.coerce.date<Date>().optional(),
});

export const SearchHistoricOrdersResponseSchema = z
  .object({
    results: HistoricOrderschema.array(),
  })
  .extend(PagedResultSchema.shape);

export const OrderJobFulfilmentTechnicianSchema = z.object({
  userId: z.string(), // Current BE user data is not uuid compliant
  name: z.string().nullish(),
  effortPercentage: z.number().min(0).max(100),
  qcGroupId: z.string().nullish(),
  qcRatingLevel: z.number().nonnegative(),
});

export const OrderIdJobIdSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
});

export const AddOrderJobFulfilmentTechnicianSchema =
  OrderJobFulfilmentTechnicianSchema.extend({
    jobType: z.string(),
  });

export const AddOrderJobFulfilmentTechnicianRequestSchema =
  OrderIdJobIdSchema.extend({
    orderId: z.uuid(),
    jobId: z.uuid(),
    technician: AddOrderJobFulfilmentTechnicianSchema,
  });

export const AddOrderJobFulfilmentTechnicianResponseSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
});

export const UpdateOrderJobFulfilmentTechnicianSchema =
  OrderJobFulfilmentTechnicianSchema.extend({
    jobType: z.string(),
    isDeleted: z.boolean(),
  });

export const UpdateOrderJobFulfilmentTechnicianRequestSchema =
  OrderIdJobIdSchema.extend({
    technician: UpdateOrderJobFulfilmentTechnicianSchema,
  });

export const UpdateOrderJobFulfilmentTechnicianResponseSchema =
  UpdateOrderJobFulfilmentTechnicianSchema.omit({
    jobType: true,
    qcGroupId: true,
  });

export const UpdateOrderJobTypeRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  jobType: z.string().nullish(),
  isSingleItem: z.boolean().optional(),
});

export const DeleteOrderJobFulfilmentTechnicianRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  userId: z.uuid(),
});

export const GetOrderJobFulfilmentTechniciansResponseSchema = z.object({
  fulfilmentTechnicians: OrderJobFulfilmentTechnicianSchema.array().nullish(),
});

export const OrderStockOrderedUnitSchema = z.object({
  sku: z.string().nullish(),
  partNumber: z.string().nullish(),
  unitBuyPrice: PriceSchema,
  stockUnitId: z.uuid(),
  status: z.string().nullish(),
});

export const OrderStockItemPartSchema = z.object({
  partId: z.uuid(),
  partDescription: z.string().nullish(),
  supplierId: z.uuid(),
  supplierName: z.string().nullish(),
  partStatus: z.string().nullish(),
  orderedUnits: z.array(OrderStockOrderedUnitSchema).nullish(),
  additionalExpenses: z.object({}).nullish(),
});

export const OrderStockItemSchema = z.object({
  itemId: z.uuid(),
  itemDescription: z.string().nullish(),
  parts: z.array(OrderStockItemPartSchema),
});

export const OrderStockSchema = z.object({
  orderId: z.uuid(),
  jobs: z.array(
    z.object({
      jobId: z.uuid(),
      jobRef: z.string(),
      items: z.array(OrderStockItemSchema),
    })
  ),
});

export const SignatureAdapterSchema = z.object({
  messageType: z.literal('signature'),
  scrollableText: z.string(),
});

export const SignatureAdapterResponseSchema = z.object({
  success: z.boolean(),
  errorMessage: z.string().nullish(),
  base64Image: z.string().nullish(),
});

export const DocumentValuesAdapterSchema = z.object({
  messageType: z.literal('document'),
  salesPersonCode: z.string(),
  systemTraceAuditNumber: z.string(),
  appReferenceNumber: z.string(),
  token: z.string().nullish(),
});

export const DocumentValuesResponseSchema = z.object({
  documentType: z.string().nullish(),
  pricingStrategy: z.string().nullish(),
  totalApr: z.string().nullish(),
  periodicApr: z.string().nullish(),
  periodicRate: z.string().nullish(),
  marginRate: z.string().nullish(),
  pricingEffectiveDate: z.string().nullish(),
  orderNumber: z.string(),
  errorMessage: z.string().nullish(),
});

export const PrintPricingStrategySchema = z.object({
  templateId: z.string().nullish(),
  fieldValues: z.object({}).catchall(z.unknown()).optional(),
});

export const PrintPricingStrategyUrlSchema = z.object({
  preSignedUrl: z.string(),
});

export const EmailReceiptSchema = z.object({
  orderId: z.string(),
  paymentId: z.string(),
});

export const ReceiveStockRequestSchema = z.object({
  purchaseOrderId: z.uuid(),
  items: z.array(
    z.object({
      id: z.uuid().optional(),
      partNumber: z.string(),
      quantity: z.int(),
      deliveryNote: z.string().nullish(),
      receivedBy: z.string(),
      shortShipped: z.boolean().nullish(),
    })
  ),
});

export const OrderCardPaymentIFrameResponseSchema = z.object({
  data: z
    .object({
      JOUR_KEY: z.string().nullish(),
    })
    .nullish(),
  message: z.string(),
  status: z.string(),
  success: z.boolean(),
  balance: z.number().optional(),
  title: z.string().nullish(),
  errorMessage: z.string().nullish(),
  number: z.string().nullish(),
  token: z.string().nullish(),
});

export const UpdateOrderBillingAddressSchema = z.object({
  orderId: z.uuid(),
  address: CustomerAddressFeSchema.omit({
    id: true,
  }),
});

export const UpsertOrderCommunicationMethodSchema = z.object({
  orderId: z.uuid(),
  customerId: z.uuid(),
  communicationMethodId: z.uuid().optional(),
  value: z.string(),
  updateCustomer: z.boolean().optional(),
});

export const OrderPartsRequestSchema = z.object({
  orderId: z.uuid(),
  partIds: z.uuid().array(),
});

export const OrderPartsResponsePartSchema = z.object({
  partId: z.uuid(),
  partDescription: z.string(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  sku: z.string(),
  partNumber: z.string(),
  status: z.string(),
  quantityOrderedExternally: z.number(),
  quantityOrderedInternally: z.number(),
  quantityRequired: z.number(),
  message: z.string().nullish(),
  type: z.string(),
  unitSellPrice: PriceSchema,
  totalPrice: PriceSchema,
});

export const OrderPartsResponseSchema = z.object({
  subTotal: z.number(),
  tax: z.number(),
  total: z.number(),
  jobs: z
    .object({
      id: z.uuid(),
      description: z.string(),
      type: z.string(),
      reference: z.string(),
      parts: OrderPartsResponsePartSchema.array(),
    })
    .array()
    .optional(),
  errors: z.string().array(),
});

const RefundOrderJobFormPartSchema = z.object({
  partId: z.uuid(),
  description: z.string().min(1),
  unitQuantity: z.number(),
  newUnitQuantity: z.number(),
  totalLinePrice: z.number().multipleOf(0.01),
  refundReasonCode: z.string().nullable(),
});

const RefundOrderJobFormItemSchema = z.object({
  itemId: z.uuid(),
  parts: RefundOrderJobFormPartSchema.array(),
});

export const RefundOrderJobFormSchema = z.object({
  jobId: z.uuid(),
  items: RefundOrderJobFormItemSchema.array(),
  total: OrderTotalSchema,
  refundReasonCode: z.string(),
});

export const RefundsItemSchema = z.object({
  jobId: z.string(),
  jobItemId: z.string(),
  partId: z.string(),
  partNumber: z.string().default('0'),
  quantity: z.number(),
  price: z.number(),
  isPart: z.boolean(),
  refundReasonCode: z.string(),
  refundDetails: z.string(),
});

export const AddRefundOrderSchema = z.object({
  orderId: z.uuid(),
  items: z.array(RefundsItemSchema),
  paymentMethod: PaymentMethodEnum,
  paymentDate: z.coerce.date<Date>(),
  paymentReference: z.string().optional(),
  traceData: z.object({}).catchall(z.unknown()).optional(),
  amount: TotalPricingSchema.optional(),
  refundReasonCode: z.string().optional(),
  refundDetails: z.string().optional(),
  originalPaymentTransactionId: z.string().optional(),
});

export const AddRefundOrderResponseSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
});

export const CaculateRefundsTotalRequestSchema = z.object({
  orderId: z.string(),
  items: RefundsItemSchema.array(),
});

export const CalculateRefundsTotalResponseSchema = z.object({
  totalParts: z.number(),
  totalLabor: z.number(),
  totalFees: z.number(),
  totalStateFees: z.number(),
  subTotal: z.number(),
  total: z.number(),
});

export const GetRefundOrderRequestSchema = z.object({
  orderId: z.uuid(),
});

export const RefundOrderFeSchema = OrderFeSchema.omit({
  customerId: true,
  currencyCode: true,
  statusReason: true,
  timeZone: true,
  jobAnswers: true,
  notes: true,
  orderSchedules: true,
  taxExemption: true,
  customer: true,
});

export const ProcessRepairOrderRequestSchema = z.object({
  orderId: z.uuid(),
  preAuthorizationCheckJobId: z.string(),
});
export const ProcessRepairOrderResponseSchema = z.object({
  approvalStatus: z.string(),
});

export const GetOrderAuditResponseSchema = z.object({
  orderId: z.uuid(),
  audit: AuditResponseSchema,
});

export const GetDeliveryMethodsRequestSchema = z.object({
  orderId: z.uuid(),
  partIds: z.uuid().array(),
});

export const DeliveryMethodOptionSchema = z.object({
  value: z.string(),
  text: z.string(),
  enabled: z.boolean(),
});

export const DeliveryMethodItemSchema = z.object({
  name: z.string(),
  default: z.string(),
  required: z.boolean(),
  hidden: z.boolean(),
  options: DeliveryMethodOptionSchema.array(),
});

export const DeliveryMethodFormItemSchema = z.object({
  value: z.string(),
  name: z.string(),
  label: z.string(),
  isEnabled: z.boolean(),
  isDefault: z.boolean(),
});

export const DeliveryPart = z.object({
  partId: z.uuid(),
  partNumber: z.string(),
  description: z.string(),
  unitQuantity: z.number(),
});

export const DeliverySupplierDetailsItemSchema = z.object({
  supplierId: z.string(),
  supplierName: z.string(),
  parts: DeliveryPart.array(),
  deliveryMethod: DeliveryMethodItemSchema.optional(),
});

export const DeliveryMethodsBeSchema = z.object({
  supplierDetails: DeliverySupplierDetailsItemSchema.array(),
});

export const DeliveryMethodSchema = z.object({
  supplierId: z.string(),
  supplierName: z.string(),
  partIds: z.uuid().array(),
  parts: DeliveryPart.array(),
  deliveryMethods: DeliveryMethodFormItemSchema.array(),
  selectedDeliveryMethod: z.string().nullable(),
});

export const DeliveryMethodsFeSchema = DeliveryMethodSchema.array();

export const SaveDeliveryMethod = z.object({
  deliveryMethod: z.object({
    name: z.string(),
    value: z.string(),
    text: z.string(),
  }),
  partIds: z.array(z.uuid()),
});

export const SaveDeliveryMethodsRequestSchema = z.object({
  orderId: z.uuid(),
  deliveryMethodParts: z.array(SaveDeliveryMethod),
});

export const SaveDeliveryMethodsRequestBeSchema = z.object({
  deliveryMethodParts: z.array(SaveDeliveryMethod),
});

export const SaveSignatureRequestSchema = z.object({
  orderId: z.uuid(),
  base64Image: z.string(),
});

///// TYPES....
export type OrderIdModel = z.infer<typeof OrderIdSchema>;
export type OrderIdNumberModel = z.infer<typeof OrderIdNumberSchema>;
export type PrintPricingStrategyModel = z.infer<
  typeof PrintPricingStrategySchema
>;
export type DocumentValuesAdapterModel = z.infer<
  typeof DocumentValuesAdapterSchema
>;
export type DocumentValuesResponsemodel = z.infer<
  typeof DocumentValuesResponseSchema
>;
export type SignatureAdapterResponseModel = z.infer<
  typeof SignatureAdapterResponseSchema
>;
export type SignatureAdapterSchemaModel = z.infer<
  typeof SignatureAdapterSchema
>;
export type EmailReceiptModel = z.infer<typeof EmailReceiptSchema>;

export type PrintPricingStrategyUrlModel = z.infer<
  typeof PrintPricingStrategyUrlSchema
>;
export type GetOrderRequestModel = z.infer<typeof GetOrderRequestSchema>;
export type OrderJobPromotionDetailsFeModel = z.infer<
  typeof OrderJobPromotionDetailsFeSchema
>;
export type OrderJobFeModel = z.infer<typeof OrderJobFeSchema>;
export type OrderJobItemFeModel = z.infer<typeof OrderJobItemFeSchema>;
export type OrderScheduleDetailsModel = z.infer<
  typeof OrderScheduleDetailsSchema
>;
export type OrderFeModel = z.infer<typeof OrderFeSchema>;
export type OrderScheduleModel = z.infer<typeof OrderScheduleSchema>;
export type CreateOrderFeModel = z.infer<typeof CreateOrderFeSchema>;
export type AddOrderRequestModel = z.infer<typeof AddOrderRequestSchema>;
export type AddOrderSourceModel = z.infer<typeof AddOrderSourceSchema>;
export type AddOrderCustomerModel = z.infer<typeof AddOrderCustomerSchema>;
export type AddOrderFulfilmentToModel = z.infer<
  typeof AddOrderFulfilmentToSchema
>;
export type OrderStockItemPartModel = z.infer<typeof OrderStockItemPartSchema>;
export type OrderStockItemModel = z.infer<typeof OrderStockItemSchema>;
export type OrderStockOrderedUnitModel = z.infer<
  typeof OrderStockOrderedUnitSchema
>;

export type FleetDriverModel = z.infer<typeof FleetDriverSchema>;
export type FleetDriverFormModel = z.infer<typeof FleetDriverFormSchema>;
export type FleetDriverIdModel = z.infer<typeof FleetDriverIdSchema>;
export type CreateRetailFleetOrderFeModel = z.infer<
  typeof CreateRetailFleetOrderFeSchema
>;
export type OrderJobItemPartFeModel = z.infer<typeof OrderJobItemPartFeSchema>;
export type OrderJobTechnicianFeModel = z.infer<
  typeof OrderJobTechnicianFeSchema
>;
export type OrderJobItemPartType = z.infer<typeof OrderJobItemPartTypeEnum>;
export type OverrideOrderJobFormPartModel = z.infer<
  typeof OverrideOrderJobFormPartSchema
>;
export type OverrideOrderJobFormModel = z.infer<
  typeof OverrideOrderJobFormSchema
>;
export type OverrideOrderJobRequestModel = z.infer<
  typeof OverrideOrderJobRequestSchema
>;
export type FleetJobPricingErrorModel = z.infer<
  typeof FleetJobPricingErrorSchema
>;
export type FleetTreadnetPriceUpdatesModel = z.infer<
  typeof FleetTreadnetPriceUpdatesSchema
>;
export type FleetUpdateJobErrorModel = z.infer<
  typeof FleetUpdateJobErrorSchema
>;
export type TechAppNotesFeModel = z.infer<typeof TechAppNotesFeSchema>;
export type OrderCustomerDetailsFeModel = z.infer<
  typeof OrderCustomerDetailsFeSchema
>;

//Driver
export type OrderDriverDetailsFeModel = z.infer<
  typeof OrderDriverDetailsFeSchema
>;
export type OrderDriverSearchRequestModel = z.infer<
  typeof OrderDriverSearchRequestSchema
>;
export type OrderDriverSearchDetailsContactPreferenceModel = z.infer<
  typeof OrderDriverSearchDetailsContactPreferenceSchema
>;
export type OrderDriverSearchDetailsModel = z.infer<
  typeof OrderDriverSearchDetailsSchema
>;
export type OrderDriverSearchResponseModel = z.infer<
  typeof OrderDriverSearchResponseSchema
>;
export type AttachFleetDriverToOrderModel = z.infer<
  typeof AttachFleetDriverToOrderSchema
>;
export type AttachFleetDriverToOrderResponseModel = z.infer<
  typeof AttachFleetDriverToOrderResponseSchema
>;
export type CompleteFleetOrderErrorModel = z.infer<
  typeof CompleteFleetOrderErrorSchema
>;
export type CompleteFleetOrderResponseModel = z.infer<
  typeof CompleteFleetOrderResponseSchema
>;
export type B2BAIApprovalModel = z.infer<typeof B2BAIApprovalSchema>;
export type OrderPreventativeMaintenanceRequestModel = z.infer<
  typeof OrderPreventativeMaintenanceRequestSchema
>;
export type OrderPreventativeMaintenanceResponseModel = z.infer<
  typeof OrderPreventativeMaintenanceResponseSchema
>;
export type CustomerTypeSelectorForm = z.infer<typeof CustomerTypeSchema>;
export type OrderSearchResponseFeModel = z.infer<
  typeof OrderSearchResponseFeSchema
>;
export type OrderSearchRequestFeModel = z.infer<
  typeof OrderSearchRequestFeSchema
>;
export type OrderSearchResultFeModel = z.infer<
  typeof OrderSearchResultFeSchema
>;
export type OrderSearchRequestV2FeModel = z.infer<
  typeof OrderSearchRequestV2FeSchema
>;

export type OrderSearchResultV2FeModel = z.infer<
  typeof OrderSearchResultV2FeSchema
>;

export type OrderSearchResultV2BeModel = z.infer<
  typeof OrderSearchResultV2BeSchema
>;
export type OrderSearchResponseV2BeModel = z.infer<
  typeof OrderSearchResponseV2BeSchema
>;

export type HistoricalOrderModel = z.infer<typeof HistoricOrderschema>;
export type SearchHistoricOrdersRequestModel = z.infer<
  typeof SearchHistoricOrdersRequestSchema
>;
export type SearchHistoricOrdersResponseModel = z.infer<
  typeof SearchHistoricOrdersResponseSchema
>;

export type UpdateOrderStatusRequestModel = z.infer<
  typeof UpdateOrderStatusRequestSchema
>;

export type AddOrderJobItemResponseModel = z.infer<
  typeof AddOrderJobItemResponseSchema
>;

export type OrderCreditReleaseStatusModel = z.infer<
  typeof OrderCreditReleaseStatusSchema
>;

export type OrderAdditionalPropertiesModel = z.infer<
  typeof OrderAdditionalPropertiesSchema
>;

export type OrderJobsForPaymentModel = z.infer<
  typeof OrderJobsForPaymentSchema
>;

export type AddOrderJobItemPaymentModel = z.infer<
  typeof AddOrderJobItemPaymentSchema
>;

export type OrderJobFulfilmentTechnicianModel = z.infer<
  typeof OrderJobFulfilmentTechnicianSchema
>;
export type GetOrderJobFulfilmentTechniciansResponseModel = z.infer<
  typeof GetOrderJobFulfilmentTechniciansResponseSchema
>;
export type AddOrderJobFulfilmentTechnicianModel = z.infer<
  typeof AddOrderJobFulfilmentTechnicianSchema
>;
export type UpdateOrderJobFulfilmentTechnicianModel = z.infer<
  typeof UpdateOrderJobFulfilmentTechnicianSchema
>;
export type UpdateOrderJobFulfilmentTechnicianResponseModel = z.infer<
  typeof UpdateOrderJobFulfilmentTechnicianResponseSchema
>;
export type UpdateOrderJobTypeRequestModel = z.infer<
  typeof UpdateOrderJobTypeRequestSchema
>;
export type DeleteOrderJobFulfilmentTechnicianRequestModel = z.infer<
  typeof DeleteOrderJobFulfilmentTechnicianRequestSchema
>;
export type OrderStockModel = z.infer<typeof OrderStockSchema>;

export type OrderBillingJobItemModel = z.infer<
  typeof OrderBillingJobItemSchema
>;
export type OrderBillingJobModel = z.infer<typeof OrderBillingJobSchema>;
export type OrderBillingModel = z.infer<typeof OrderBillingSchema>;

export const SystemTraceAuditNumberSchema = z.object({
  value: z.number(),
});
export type SystemTraceAuditNumberModel = z.infer<
  typeof SystemTraceAuditNumberSchema
>;

// Payments adapter IFrame Data
export type OrderPaymentsAdapterTypeModel = z.infer<
  typeof OrderPaymentsAdapterTypeEnum
>;
export type OrderPaymentsAdapterModel = z.infer<
  typeof OrderPaymentsAdapterSchema
>;
export type OrderVoidsAdapterModel = z.infer<typeof OrderVoidsAdapterSchema>;
export type OrderGiftCardAdapterModel = z.infer<
  typeof OrderGiftCardAdapterSchema
>;
export type OrderRefundsAdapterModel = z.infer<
  typeof OrderRefundsAdapterSchema
>;

export type OrderRefundWithTokenAdapterModel = z.infer<
  typeof OrderRefundWithTokenAdapterSchema
>;

export type OrderCardPaymentIFrameResponseModel = z.infer<
  typeof OrderCardPaymentIFrameResponseSchema
>;

export type ReceiveStockRequestType = z.infer<typeof ReceiveStockRequestSchema>;
export type UpdateOrderBillingAddressModel = z.infer<
  typeof UpdateOrderBillingAddressSchema
>;
export type UpsertOrderCommunicationMethodModel = z.infer<
  typeof UpsertOrderCommunicationMethodSchema
>;

export type OrderPartsRequestType = z.infer<typeof OrderPartsRequestSchema>;
export type OrderPartsResponseType = z.infer<typeof OrderPartsResponseSchema>;
export type OrderPartsResponsePartType = z.infer<
  typeof OrderPartsResponsePartSchema
>;

export type CalculateRefundsTotalRequestModel = z.infer<
  typeof CaculateRefundsTotalRequestSchema
>;

export type CalculateRefundsTotalResponseModel = z.infer<
  typeof CalculateRefundsTotalResponseSchema
>;

export type RefundsItemModel = z.infer<typeof RefundsItemSchema>;

export type RefundOrderJobFormPartModel = z.infer<
  typeof RefundOrderJobFormPartSchema
>;
export type RefundOrderJobFormModel = z.infer<typeof RefundOrderJobFormSchema>;

export type AddRefundOrderModel = z.infer<typeof AddRefundOrderSchema>;

export type AddRefundOrderResponseModel = z.infer<
  typeof AddRefundOrderResponseSchema
>;
export type GetDeliveryMethodsRequestModel = z.infer<
  typeof GetDeliveryMethodsRequestSchema
>;

export type GetDeliveryMethodsResponseModel = z.infer<
  typeof DeliveryMethodsBeSchema
>;

export type DeliveryMethodsModel = z.infer<typeof DeliveryMethodsFeSchema>;

export type DeliveryMethodType = z.infer<typeof DeliveryMethodSchema>;

export type DeliveryMethodFormType = z.infer<
  typeof DeliveryMethodFormItemSchema
>;

export type SaveDeliveryMethodModel = z.infer<typeof SaveDeliveryMethod>;

export type SaveDeliveryMethodsRequestModel = z.infer<
  typeof SaveDeliveryMethodsRequestSchema
>;

export type SaveSignatureRequestModel = z.infer<
  typeof SaveSignatureRequestSchema
>;

export type RefundOrderFeModel = z.infer<typeof RefundOrderFeSchema>;
export type GetOrderAuditResponseModel = z.infer<
  typeof GetOrderAuditResponseSchema
>;

export type OrderAlertCode = z.infer<typeof OrderAlertCodeEnum>;

// Process Repair Order
export type ProcessRepairOrderRequestModel = z.infer<
  typeof ProcessRepairOrderRequestSchema
>;
export type ProcessRepairOrderResponseModel = z.infer<
  typeof ProcessRepairOrderResponseSchema
>;
export type OrderAlertModel = z.infer<typeof OrderAlertSchema>;
export type OrderJobAlertModel = z.infer<typeof OrderJobAlertSchema>;
export type SendOrderInvoiceModel = z.infer<typeof SendOrderInvoiceSchema>;

export const ResequenceJobsSchema = z.object({
  orderId: z.uuid(),
  jobIds: z.array(z.uuid()),
});
export type ResequenceJobsModel = z.infer<typeof ResequenceJobsSchema>;
